import { CircleHelp, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Sheet, SheetContent } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';
import { EntitySwitcher } from './EntitySwitcher';
import { SETTINGS_ITEM } from './nav';
import { useNavItems } from './useNavItems';
import { badgeLabel, useNavBadges } from './useNavBadges';


/** Bottom navigation for small screens: four destinations + More (sheet with the rest). */
export const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const badges = useNavBadges();
  const items = useNavItems();
  const PRIMARY = items.filter((i) => i.mobile);
  const MORE = [...items.filter((i) => !i.mobile), SETTINGS_ITEM];
  const openHelp = useAppStore((s) => s.openHelp);
  const { pathname } = useLocation();
  const moreActive = MORE.some((i) => pathname.startsWith(i.to));
  const tab = 'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium';
  return (
    <>
      <nav
        data-print-hide
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        <ul className="flex">
          {PRIMARY.map((item) => {
            const Icon = item.icon;
            const count = item.badge ? badges[item.badge] : 0;
            return (
              <li key={item.id} className="flex flex-1">
                <NavLink to={item.to} end={item.to === '/'} className={({ isActive }) => cn(tab, isActive ? 'text-primary' : 'text-text-muted')}>
                  <span className="relative">
                    <Icon className="size-5" aria-hidden />
                    {count > 0 && item.badge ? (
                      <span className="absolute -right-2 -top-1 rounded-full bg-accent px-1 text-[10px] font-semibold leading-4 text-accent-foreground">
                        <span aria-hidden>{count > 99 ? '99+' : count}</span>
                        <span className="sr-only">{badgeLabel(item.badge, count)}</span>
                      </span>
                    ) : null}
                  </span>
                  {item.label}
                </NavLink>
              </li>
            );
          })}
          <li className="flex flex-1">
            <button type="button" className={cn(tab, moreActive ? 'text-primary' : 'text-text-muted')} onClick={() => setOpen(true)} aria-haspopup="dialog">
              <MoreHorizontal className="size-5" aria-hidden />
              More
            </button>
          </li>
        </ul>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" title="More">
          <div className="mb-4">
            <EntitySwitcher />
          </div>
          <ul className="grid grid-cols-3 gap-2">
            {MORE.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn('flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-lg border border-border text-sm', isActive ? 'bg-primary-soft text-primary' : 'text-text')
                    }
                  >
                    <Icon className="size-5" aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openHelp(null);
                }}
                className="flex min-h-20 w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-border text-sm text-text"
              >
                <CircleHelp className="size-5" aria-hidden />
                Help
              </button>
            </li>
          </ul>
        </SheetContent>
      </Sheet>
    </>
  );
};
